import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UploadCloud, Trash2, Clock, CheckCircle2, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  pending: { cls: "bg-mustard/25 text-earth", Icon: Clock, label: "Pending review" },
  approved: { cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, label: "Approved" },
  rejected: { cls: "bg-red-100 text-red-700", Icon: XCircle, label: "Rejected" },
};

function SlotRow({ slot, images, pid, onChanged, canEdit }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const multiple = slot === "Additional Images";

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/brand/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/brand/products/${pid}/images`, { slot, url: `${BACKEND_URL}${data.url}` });
      toast.success(`${slot} uploaded — pending review`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const del = async (img) => { if (!window.confirm("Remove this image?")) return; try { await api.delete(`/brand/products/${pid}/images/${img.id}`); toast.success("Image removed"); onChanged(); } catch { toast.error("Failed"); } };

  return (
    <div className="border border-sage/25 rounded-xl p-3" data-testid={`slot-${slot.replace(/\s/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-500 text-forest">{slot}</p>
        {canEdit && (multiple || images.length === 0) && (
          <button onClick={() => ref.current?.click()} disabled={busy} className="text-xs inline-flex items-center gap-1 text-olive hover:text-forest" data-testid={`upload-${slot.replace(/\s/g, "-").toLowerCase()}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
      </div>
      {images.length === 0 ? <p className="text-xs text-brown/40">No image yet</p> : (
        <div className="flex flex-wrap gap-2">
          {images.map((img) => {
            const st = STATUS[img.status] || STATUS.pending;
            return (
              <div key={img.id} className="w-24" data-testid={`slot-image-${img.id}`}>
                <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-sage/30 bg-sand">
                  <img src={img.url} alt={slot} className="w-full h-full object-cover" />
                  {canEdit && <button onClick={() => del(img)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center" data-testid={`delete-image-${img.id}`}><Trash2 className="w-3.5 h-3.5 text-terracotta" /></button>}
                </div>
                <span className={`mt-1 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${st.cls}`}><st.Icon className="w-3 h-3" /> {st.label}</span>
                {img.status === "rejected" && img.reject_reason && <p className="text-[10px] text-red-600 mt-0.5 leading-tight" data-testid={`reject-reason-${img.id}`}>Reason: {img.reject_reason}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function BrandProductImages({ product, open, onOpenChange, onChanged, canEdit = true }) {
  const [data, setData] = useState(null);
  const pid = product?.id;
  const load = () => pid && api.get(`/brand/products/${pid}/images`).then((r) => setData(r.data));
  useEffect(() => { if (open && pid) { setData(null); load(); } }, [open, pid]);

  const changed = () => { load(); onChanged && onChanged(); };
  const bySlot = (slot) => (data?.images || []).filter((i) => i.slot === slot);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Product Images — {product?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-brown/60 -mt-2">All images go to LIVENEAT for review and are not shown to shoppers until approved.{!canEdit && " You have read-only access."}</p>
        {!data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.slots.map((slot) => <SlotRow key={slot} slot={slot} images={bySlot(slot)} pid={pid} onChanged={changed} canEdit={canEdit} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
